import { observer, use$ } from '@legendapp/state/react';
import { SAMPLE, C, momMeta, depthMeta, num } from '../data/sample';
import type { App, SampleData } from '../data/sample';
import { state$, openFunc } from '../state';

interface Feat {
  label: string;
  mark: string;
  bg: string;
  fg: string;
}
interface Tier {
  name: string;
  price: string;
  feats: Feat[];
}

function tiersFor(a: App, d: SampleData): Tier[] {
  const fns = d.functions;
  const gatedNames = fns.filter((_, fi) => a.gated[fi]).map((f) => f.name);
  const coreNames = fns
    .filter((_, fi) => a.depths[fi]! > 0 && !a.gated[fi])
    .slice(0, 3)
    .map((f) => f.name);
  const mk = (label: string, gated: boolean): Feat => ({
    label,
    mark: gated ? '$' : '✓',
    bg: gated ? '#fff1e6' : '#ebebeb',
    fg: gated ? '#ea580c' : C.ink2,
  });
  const idx = d.apps.indexOf(a);
  return [
    {
      name: 'Free',
      price: '$0',
      feats: [mk(coreNames[0] ?? 'Basic reviews', false), mk('25 orders/mo', false)],
    },
    {
      name: 'Starter',
      price: '$' + (9 + idx) + '/mo',
      feats: coreNames
        .slice(0, 2)
        .map((c) => mk(c, false))
        .concat([mk('Remove branding', false)]),
    },
    {
      name: 'Pro',
      price: '$' + (19 + idx * 2) + '/mo',
      feats: gatedNames.slice(0, 3).map((g) => mk(g, true)),
    },
    {
      name: 'Scale',
      price: '$' + (49 + idx * 4) + '/mo',
      feats: gatedNames
        .slice(0, 4)
        .map((g) => mk(g, true))
        .concat([mk('Priority support', false)]),
    },
  ];
}

interface Complaint {
  label: string;
  count: number;
}
interface Request {
  label: string;
  count: number;
  trendArrow: string;
  trendFg: string;
}

function voiceFor(a: App, d: SampleData): { complaints: Complaint[]; requests: Request[] } {
  const fns = d.functions;
  const weak: Complaint[] = fns
    .filter((_, fi) => a.depths[fi]! > 0 && a.depths[fi]! <= 2)
    .slice(0, 3)
    .map((f) => ({
      label: `“${f.name}” has limited customization`,
      count: 10 + ((d.apps.indexOf(a) + fns.indexOf(f)) % 30),
    }));
  const missing: Request[] = fns
    .filter((_, fi) => a.depths[fi] === 0)
    .map((f) => ({
      label: `Add “${f.name}”`,
      count: 8 + ((fns.indexOf(f) * 5) % 25),
      trendArrow: '↑',
      trendFg: '#008060',
    }));
  const fallbackReq: Request[] = [
    { label: 'Integrate with loyalty apps', count: 21, trendArrow: '↑', trendFg: '#008060' },
    { label: 'Better multi-language support', count: 14, trendArrow: '→', trendFg: '#6d7175' },
  ];
  return {
    complaints: weak.length
      ? weak
      : [
          { label: 'Widget UI is hard to customize', count: 17 },
          { label: 'Slow support response', count: 11 },
        ],
    requests: (missing.length ? missing : fallbackReq).slice(0, 3),
  };
}

export const AppProfile = observer(function AppProfile() {
  const appId = use$(state$.appId);
  const d = SAMPLE;
  const a = d.apps.find((x) => x.id === appId) ?? d.apps[0]!;
  const mm = momMeta(a.mom);

  // momentum chart (ported verbatim)
  const n = 12;
  const pts: number[] = [];
  let v = 40;
  for (let i = 0; i < n; i++) {
    const drift = a.mom === 'rising' ? i * 4 : a.mom === 'falling' ? (n - i) * 3 : 0;
    const wob = (i * 7 + a.reviews) % 9;
    v = 20 + drift + wob;
    pts.push(v);
  }
  const maxV = Math.max(...pts);
  const W = 600;
  const H = 120;
  const coords = pts.map((p, i) => [(i / (n - 1)) * W, H - (p / maxV) * (H - 12) - 4] as const);
  const chartLine = coords.map((c) => c[0].toFixed(0) + ',' + c[1].toFixed(0)).join(' ');
  const chartArea =
    '0,' +
    H +
    ' ' +
    coords.map((c) => c[0].toFixed(0) + ',' + c[1].toFixed(0)).join(' ') +
    ' ' +
    W +
    ',' +
    H;

  const tiers = tiersFor(a, d);
  const voice = voiceFor(a, d);

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', animation: 'nl-fade .3s ease' }}>
      {/* header */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e1e3e5',
          borderRadius: 16,
          padding: 22,
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 15,
            background: a.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 24,
            color: '#303030',
          }}
        >
          {a.initials}
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>
              {a.name}
            </h1>
            <a
              href="#"
              style={{ fontSize: 12.5, color: '#303030', textDecoration: 'none', fontWeight: 600 }}
            >
              listing ↗
            </a>
          </div>
          <div style={{ fontSize: 13, color: '#6d7175' }}>{a.vendor}</div>
        </div>
        <div style={{ display: 'flex', gap: 26 }}>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 21, fontWeight: 700 }}>
              {num(a.reviews)}
            </div>
            <div style={{ fontSize: 11, color: '#6d7175', fontWeight: 600 }}>reviews</div>
          </div>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono'", fontSize: 21, fontWeight: 700 }}>
              ★ {a.rating}
            </div>
            <div style={{ fontSize: 11, color: '#6d7175', fontWeight: 600 }}>rating</div>
          </div>
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono'",
                fontSize: 21,
                fontWeight: 700,
                color: mm.fg,
              }}
            >
              {mm.arrow} {mm.label}
            </div>
            <div style={{ fontSize: 11, color: '#6d7175', fontWeight: 600 }}>momentum</div>
          </div>
        </div>
      </div>

      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, alignItems: 'start' }}
      >
        {/* momentum chart */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #e1e3e5',
            borderRadius: 16,
            padding: 20,
            gridColumn: '1 / -1',
          }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 14px' }}>
            Review velocity (12 mo)
          </h2>
          <svg viewBox="0 0 600 130" style={{ width: '100%', height: 120, overflow: 'visible' }}>
            <polyline points={chartArea} fill="url(#nlgrad)" stroke="none" />
            <polyline
              points={chartLine}
              fill="none"
              stroke="#2c6ecb"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <defs>
              <linearGradient id="nlgrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2c6ecb" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#2c6ecb" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* pricing */}
        <div
          style={{ background: '#fff', border: '1px solid #e1e3e5', borderRadius: 16, padding: 20 }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 14px' }}>
            Pricing tiers & gated features
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tiers.map((t) => (
              <div
                key={t.name}
                style={{ border: '1px solid #e1e3e5', borderRadius: 12, padding: '13px 15px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    marginBottom: 9,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{t.name}</span>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono'",
                      fontSize: 15,
                      fontWeight: 700,
                      color: '#303030',
                    }}
                  >
                    {t.price}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {t.feats.map((f, fi) => (
                    <span
                      key={fi}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 11.5,
                        padding: '3px 9px',
                        borderRadius: 7,
                        background: f.bg,
                        color: f.fg,
                        fontWeight: 600,
                      }}
                    >
                      {f.mark} {f.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* coverage */}
        <div
          style={{ background: '#fff', border: '1px solid #e1e3e5', borderRadius: 16, padding: 20 }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 14px' }}>
            Function coverage in this niche
          </h2>
          {d.functions.map((f, fi) => {
            const depth = a.depths[fi]!;
            const dm = depthMeta(depth);
            const sc = a.scores[fi];
            return (
              <div
                key={f.id}
                onClick={() => openFunc(f.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  padding: '8px 0',
                  cursor: 'pointer',
                }}
              >
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{f.name}</span>
                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 14 }}>
                  <span style={{ width: 5, height: 6, borderRadius: 1, background: dm.bars[0] }} />
                  <span style={{ width: 5, height: 10, borderRadius: 1, background: dm.bars[1] }} />
                  <span style={{ width: 5, height: 14, borderRadius: 1, background: dm.bars[2] }} />
                </div>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono'",
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: sc === null ? '#c2c6cf' : C.ink2,
                    width: 26,
                    textAlign: 'right',
                  }}
                >
                  {sc === null ? '—' : sc}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* voice */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e1e3e5',
          borderRadius: 16,
          padding: 20,
          marginTop: 18,
        }}
      >
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 16px' }}>Voice of customer</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#d72c0d',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 11,
              }}
            >
              Complaint clusters
            </div>
            {voice.complaints.map((cp, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  padding: '8px 0',
                  borderBottom: '1px solid #f1f1f1',
                }}
              >
                <span style={{ fontSize: 12.5, color: '#616161' }}>{cp.label}</span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono'",
                    fontSize: 11,
                    color: '#a5abb5',
                    flexShrink: 0,
                  }}
                >
                  {cp.count}×
                </span>
              </div>
            ))}
          </div>
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#303030',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 11,
              }}
            >
              Feature request
            </div>
            {voice.requests.map((rq, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  padding: '8px 0',
                  borderBottom: '1px solid #f1f1f1',
                }}
              >
                <span style={{ fontSize: 12.5, color: '#616161' }}>{rq.label}</span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 11,
                    fontWeight: 700,
                    color: rq.trendFg,
                    flexShrink: 0,
                  }}
                >
                  <span style={{ fontFamily: "'JetBrains Mono'" }}>{rq.trendArrow}</span>
                  {rq.count}×
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});
